import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Select, { components, OptionProps } from "react-select";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { AuthContext } from "../../../context/authcontext";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import Swal from "sweetalert2";
import "./ApprovalPolicy.css";
import { CurrencySelect } from "../../../components/currency";
import ApprovalPolicyGuide from "./ApprovalPolicyGuide";
import {
  ApprovalPolicyService,
  ApprovalRuleOperator,
  ApprovalActorType,
  ApprovalStageCondition,
  EntityField,
  TenantUser,
  ApprovalPolicyDetailAPI,
  ApprovalRuleAPI,
} from "./approvalPolicy.service";

const CustomOption = (props: OptionProps<any>) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          style={{ marginRight: 8, cursor: "pointer" }}
        />
        <label style={{ cursor: "pointer", margin: 0 }}>{props.label}</label>
      </div>
    </components.Option>
  );
};

interface RuleItem {
  id: string;
  entity_field_name: string;
  operator_key: string;
  value_json: Record<string, any>;
  priority: number;
  rule_group_key: string;
  rule_join_key: string | null;
  allowedOperators: string[];
  apiId?: string | number;
}

interface StageItem {
  id: string;
  name: string;
  order: number;
  condition: string;
  approver: string;
  apiId?: string | number;
  approverIdentities?: (string | number)[];
}

interface Policy {
  id: string;
  title: string;
  isActive: boolean;
  requiresManagerApproval: boolean;
  priorityOrder: number;
  rules: RuleItem[];
  stages: StageItem[];
}

type SelectedNode = "title" | "rules" | "stages" | null;

const genId = () => Math.random().toString(36).substring(2, 11);

const genGroupKey = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const OPERATOR_DISPLAY: Record<string, string> = {
  BETWEEN: "Between",
  EQUAL: "Equal",
  GREATER_THAN: "Greater Than",
  LESS_THAN: "Less Than",
};

const DATE_FIELD_KEYWORDS = ["date", "datetime", "time", "created_at", "updated_at"];

const isDateField = (fieldName: string) => {
  return DATE_FIELD_KEYWORDS.some((kw) => fieldName.toLowerCase().includes(kw));
};

const ApprovalPolicy: React.FC = () => {
  const route = all_routes;
  const { selectedTenantId, auth } = useContext(AuthContext);

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || !selectedTenantId) return null;
    const currentTenant = auth.tenants.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    );
    return currentTenant?.role_key || null;
  }, [auth?.tenants, selectedTenantId]);

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [operatorOptions, setOperatorOptions] = useState<ApprovalRuleOperator[]>([]);
  const [approverOptions, setApproverOptions] = useState<ApprovalActorType[]>([]);
  const [conditionOptions, setConditionOptions] = useState<ApprovalStageCondition[]>([]);
  const [entityFields, setEntityFields] = useState<EntityField[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [view, setView] = useState<"list" | "builder">("list");
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [viewDetailPolicy, setViewDetailPolicy] = useState<ApprovalPolicyDetailAPI | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentPolicyId, setCurrentPolicyId] = useState<string | number | null>(null);

  const [builderTitle, setBuilderTitle] = useState("");
  const [builderActive, setBuilderActive] = useState(true);
  const [builderRequireManagerApproval, setBuilderRequireManagerApproval] = useState(false);
  const [builderPriorityOrder, setBuilderPriorityOrder] = useState<number | "">(1);
  const [builderRules, setBuilderRules] = useState<RuleItem[]>([]);
  const [builderStages, setBuilderStages] = useState<StageItem[]>([]);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [fetchedRulesForPolicyId, setFetchedRulesForPolicyId] = useState<string | number | null>(null);
  const [loadingRules, setLoadingRules] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [stageApproverIdentities, setStageApproverIdentities] = useState<(string | number)[]>([]);

  // Form controls for stages
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [savingStage, setSavingStage] = useState(false);
  const [stageName, setStageName] = useState("");
  const [stageOrder, setStageOrder] = useState(1);
  const [stageCondition, setStageCondition] = useState("");
  const [stageApprover, setStageApprover] = useState("");
  const [externalEmailInput, setExternalEmailInput] = useState("");

  const titleSnapshotRef = useRef<{ title: string; active: boolean; managerApproval: boolean; priorityOrder: number | "" }>({ title: "", active: true, managerApproval: false, priorityOrder: 1 });
  const [titleDirty, setTitleDirty] = useState(false);

  const snapshotTitle = useCallback(() => {
    titleSnapshotRef.current = {
      title: builderTitle,
      active: builderActive,
      managerApproval: builderRequireManagerApproval,
      priorityOrder: builderPriorityOrder,
    };
    setTitleDirty(false);
  }, [builderTitle, builderActive, builderRequireManagerApproval, builderPriorityOrder]);

  const isTitleDirty = useCallback(() => {
    const snap = titleSnapshotRef.current;
    return (
      builderTitle !== snap.title ||
      builderActive !== snap.active ||
      builderRequireManagerApproval !== snap.managerApproval ||
      builderPriorityOrder !== snap.priorityOrder
    );
  }, [builderTitle, builderActive, builderRequireManagerApproval, builderPriorityOrder]);

  useEffect(() => {
    setTitleDirty(isTitleDirty());
  }, [builderTitle, builderActive, builderRequireManagerApproval, isTitleDirty]);

  const getFilteredOperators = useCallback((allowedOperators: string[]) => {
    if (allowedOperators.length === 0) return operatorOptions;
    return operatorOptions.filter((op) =>
      allowedOperators.includes(op.approval_rule_operator_key.toUpperCase()) ||
      allowedOperators.includes(op.approval_rule_operator_key)
    );
  }, [operatorOptions]);

  const updateRule = (id: string, updates: Partial<RuleItem>) => {
    setBuilderRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  useEffect(() => {
    if (!selectedTenantId) return;
    setLoadingPolicies(true);
    Promise.all([
      ApprovalPolicyService.fetchPolicies(selectedTenantId),
      ApprovalPolicyService.fetchRuleOperators(),
      ApprovalPolicyService.fetchActorTypes(),
      ApprovalPolicyService.fetchStageConditions(),
      ApprovalPolicyService.fetchEntityFields("proposal"),
    ])
      .then(([results, operators, actors, conditions, fields]) => {
        const fetchedPolicies: Policy[] = results.map((item) => ({
          id: String(item.approval_policy_id),
          title: item.approval_policy_name,
          isActive: item.is_active,
          requiresManagerApproval: item.is_manager_approval_needed,
          priorityOrder: item.priority_order ?? 1,
          rules: [],
          stages: [],
        }));
        setPolicies(fetchedPolicies);
        setOperatorOptions(operators);
        setApproverOptions(actors);
        setConditionOptions(conditions);
        setEntityFields(fields);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadingPolicies(false);
      });
  }, [selectedTenantId]);

  useEffect(() => {
    if ((stageApprover === "SALESUSER" || stageApprover === "CLIENT_ADMIN") && selectedTenantId) {
      ApprovalPolicyService.fetchTenantUsers(selectedTenantId)
        .then(setTenantUsers)
        .catch(err => console.error("Failed to fetch tenant users", err));
    }
  }, [stageApprover, selectedTenantId]);

  const resetBuilder = () => {
    setBuilderTitle("");
    setBuilderActive(true);
    setBuilderRequireManagerApproval(false);
    setBuilderPriorityOrder(1);
    setBuilderRules([]);
    setBuilderStages([]);
    setSelectedNode(null);
    setExpandedRuleId(null);
    setFetchedRulesForPolicyId(null);
    setEditingStageId(null);
    setStageName("");
    setStageOrder(1);
    setStageCondition(conditionOptions.length > 0 ? conditionOptions[0].approval_stage_condition_key : "");
    setStageApprover(approverOptions.length > 0 ? approverOptions[0].approval_actor_type_key : "");
    setExternalEmailInput("");
    setEditingPolicy(null);
    setCurrentPolicyId(null);
    setTitleDirty(false);
    titleSnapshotRef.current = { title: "", active: true, managerApproval: false, priorityOrder: 1 };
  };

  const getApproverInitials = (name?: string | null, email?: string | null) => {
    const source = (name || email || "").trim();
    if (!source) return "?";
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const openCreate = () => {
    resetBuilder();
    setView("builder");
    setSelectedNode("title");
    titleSnapshotRef.current = { title: "", active: true, managerApproval: false, priorityOrder: 1 };
  };

  const openEdit = async (policy: Policy) => {
    setEditingPolicy(policy);
    setBuilderTitle(policy.title);
    setBuilderActive(policy.isActive);
    setBuilderRequireManagerApproval(policy.requiresManagerApproval);
    setBuilderPriorityOrder(policy.priorityOrder ?? 1);
    setSelectedNode("title");
    setView("builder");
    setOpenMenuId(null);
    setCurrentPolicyId(policy.id);
    titleSnapshotRef.current = {
      title: policy.title,
      active: policy.isActive,
      managerApproval: policy.requiresManagerApproval,
      priorityOrder: policy.priorityOrder ?? 1,
    };
    setTitleDirty(false);

    setBuilderRules([]);
    setBuilderStages([]);

    if (!selectedTenantId) return;

    try {
      const fetchedRules = await ApprovalPolicyService.fetchRulesByPolicy(selectedTenantId, policy.id);
      const transformedRules: RuleItem[] = fetchedRules.map((r) => {
        const field = entityFields.find(f => f.field_name === r.entity_field_name);
        return {
          id: genId(),
          apiId: r.approval_policy_rule_id,
          entity_field_name: r.entity_field_name,
          operator_key: r.operator_key,
          value_json: r.value_json || {},
          priority: r.priority,
          rule_group_key: r.rule_group_key,
          rule_join_key: r.rule_join_key === null ? "" : r.rule_join_key,
          allowedOperators: field?.allowed_operators || [],
        };
      });
      setBuilderRules(transformedRules);

      const fetchedStages = await ApprovalPolicyService.fetchStagesByPolicy(selectedTenantId, policy.id);
      const transformedStages: StageItem[] = [];

      for (const stage of fetchedStages) {
        const conditionKey = conditionOptions.find(c => c.approval_stage_condition_id === stage.approval_stage_condition_id)?.approval_stage_condition_key || "";

        let actorKey = "";
        let identities: (string | number)[] = [];

        try {
          const approvers = await ApprovalPolicyService.fetchStageApprovers(selectedTenantId, stage.approval_policy_stage_id);
          if (approvers.length > 0) {
            const approver = approvers[0];
            actorKey = approverOptions.find(a => a.approval_actor_type_id === approver.approval_actor_type_id)?.approval_actor_type_key || "";
            identities = approver.approver_identities || [];
          }
        } catch (err) {
          console.error("Failed to fetch stage approver", err);
        }

        transformedStages.push({
          id: genId(),
          apiId: stage.approval_policy_stage_id,
          name: stage.stage_name,
          order: stage.stage_order,
          condition: conditionKey,
          approver: actorKey,
          approverIdentities: identities,
        });
      }
      transformedStages.sort((a, b) => a.order - b.order);
      setBuilderStages(transformedStages);
    } catch (err) {
      console.error("Failed to load policy rules or stages", err);
      setBuilderRules([...policy.rules]);
      setBuilderStages([...policy.stages]);
    }
  };

  const confirmUnsavedChanges = async (): Promise<"save" | "discard" | "cancel"> => {
    const result = await Swal.fire({
      title: "Unsaved Changes",
      text: "You have unsaved changes. What would you like to do?",
      icon: "warning",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonColor: "#6366f1",
      denyButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Save Changes",
      denyButtonText: "Discard",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) return "save";
    if (result.isDenied) return "discard";
    return "cancel";
  };

  const saveTitleSection = async () => {
    if (!builderTitle.trim()) {
      await showErrorAlert("Please enter a policy title.");
      return false;
    }

    const parsedPriority = Number(builderPriorityOrder) || 1;
    if (policies.some(p => p.priorityOrder === parsedPriority && String(p.id) !== String(currentPolicyId || editingPolicy?.id))) {
      await showErrorAlert("A policy with this priority order already exists.");
      return false;
    }

    setSavingPolicy(true);
    try {
      if (editingPolicy || currentPolicyId) {
        const id = currentPolicyId || editingPolicy!.id;
        const updated = await ApprovalPolicyService.updatePolicy(id, {
          approval_policy_name: builderTitle.trim(),
          is_active: builderActive,
          is_manager_approval_needed: builderRequireManagerApproval,
          priority_order: parsedPriority,
          related_entity_name: "proposal",
          tenant_id: selectedTenantId!,
          updated_by: auth?.user_id,
        });
        setCurrentPolicyId(updated.approval_policy_id);
        const policyData: Policy = {
          id: String(updated.approval_policy_id),
          title: updated.approval_policy_name,
          isActive: updated.is_active,
          requiresManagerApproval: updated.is_manager_approval_needed,
          priorityOrder: parsedPriority,
          rules: builderRules,
          stages: builderStages,
        };
        setPolicies((prev) =>
          prev.map((p) => (p.id === String(id) ? policyData : p))
        );
        if (!editingPolicy) {
          setEditingPolicy(policyData);
        }
      } else {
        const created = await ApprovalPolicyService.createPolicy({
          approval_policy_name: builderTitle.trim(),
          is_active: builderActive,
          is_manager_approval_needed: builderRequireManagerApproval,
          priority_order: parsedPriority,
          related_entity_name: "proposal",
          tenant_id: selectedTenantId!,
          created_by: auth?.user_id,
          updated_by: auth?.user_id,
        });
        setCurrentPolicyId(created.approval_policy_id);
        const policyData: Policy = {
          id: String(created.approval_policy_id),
          title: created.approval_policy_name,
          isActive: created.is_active,
          requiresManagerApproval: created.is_manager_approval_needed,
          priorityOrder: parsedPriority,
          rules: builderRules,
          stages: builderStages,
        };
        setPolicies((prev) => [...prev, policyData]);
        setEditingPolicy(policyData);
      }
      snapshotTitle();
      await showSuccessAlert("Policy saved successfully.");
      return true;
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to save policy.");
      return false;
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleNodeSwitch = async (target: SelectedNode) => {
    if (selectedNode === target) return;

    if (selectedNode === "title" && titleDirty) {
      const action = await confirmUnsavedChanges();
      if (action === "save") {
        const saved = await saveTitleSection();
        if (!saved) return;
      } else if (action === "cancel") {
        return;
      } else {
        const snap = titleSnapshotRef.current;
        setBuilderTitle(snap.title);
        setBuilderActive(snap.active);
        setBuilderRequireManagerApproval(snap.managerApproval);
        setBuilderPriorityOrder(snap.priorityOrder);
        setTitleDirty(false);
      }
    }

    if (target === "rules" && currentPolicyId && fetchedRulesForPolicyId !== currentPolicyId) {
      setLoadingRules(true);
      try {
        const rulesData = await ApprovalPolicyService.fetchRulesByPolicy(selectedTenantId!, currentPolicyId);
        const mappedRules: RuleItem[] = rulesData.map(r => {
          const field = entityFields.find(f => f.field_name === r.entity_field_name);
          return {
            id: genId(),
            apiId: r.approval_policy_rule_id,
            entity_field_name: r.entity_field_name,
            operator_key: r.operator_key,
            value_json: r.value_json || {},
            priority: r.priority,
            rule_group_key: r.rule_group_key,
            rule_join_key: r.rule_join_key === null ? "" : r.rule_join_key,
            allowedOperators: field?.allowed_operators || [],
          };
        });
        setBuilderRules(mappedRules);
        setFetchedRulesForPolicyId(currentPolicyId);
      } catch (err) {
        console.error("Failed to fetch rules", err);
        await showErrorAlert("Failed to fetch rules for this policy.");
      } finally {
        setLoadingRules(false);
      }
    }

    if (selectedNode === "rules" && expandedRuleId) {
      setExpandedRuleId(null);
    }

    setSelectedNode(target);
  };

  const handleDelete = async (id: string) => {
    setOpenMenuId(null);
    const result = await Swal.fire({
      title: "Delete Policy?",
      text: "This action cannot be undone. All rules and stages will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await ApprovalPolicyService.deletePolicy(id);
        setPolicies((prev) => prev.filter((p) => p.id !== id));
        await showSuccessAlert("Policy deleted successfully.");
      } catch (err) {
        console.error(err);
        await showErrorAlert("Failed to delete policy.");
      }
    }
  };

  const handleViewDetails = async (policy: Policy) => {
    setOpenMenuId(null);
    if (!selectedTenantId) return;
    setLoadingDetail(true);
    try {
      const data = await ApprovalPolicyService.fetchPolicyDetail(selectedTenantId, policy.id);
      setViewDetailPolicy(data);
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to fetch policy details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const addRule = () => {
    const defaultField = entityFields.length > 0 ? entityFields[0].field_name : "";
    const defaultAllowed = entityFields.length > 0 ? entityFields[0].allowed_operators : [];
    const matchingOps = operatorOptions.filter((op) =>
      defaultAllowed.includes(op.approval_rule_operator_key.toUpperCase()) ||
      defaultAllowed.includes(op.approval_rule_operator_key)
    );
    const defaultOp = matchingOps.length > 0 ? matchingOps[0].approval_rule_operator_key : "";

    const newRule: RuleItem = {
      id: genId(),
      entity_field_name: defaultField,
      operator_key: defaultOp,
      value_json: {},
      priority: 1,
      rule_group_key: "",
      rule_join_key: "",
      allowedOperators: defaultAllowed,
    };
    setBuilderRules((prev) => [...prev, newRule]);
    setExpandedRuleId(newRule.id);
  };

  const handleFieldChange = (id: string, fieldName: string) => {
    const field = entityFields.find((f) => f.field_name === fieldName);
    const allowed = field?.allowed_operators || [];
    const matchingOps = operatorOptions.filter((op) =>
      allowed.includes(op.approval_rule_operator_key.toUpperCase()) ||
      allowed.includes(op.approval_rule_operator_key)
    );
    const defaultOp = matchingOps.length > 0 ? matchingOps[0].approval_rule_operator_key : "";
    updateRule(id, {
      entity_field_name: fieldName,
      operator_key: defaultOp,
      value_json: {},
      allowedOperators: allowed,
    });
  };

  const handleOperatorChange = (id: string, opKey: string) => {
    updateRule(id, { operator_key: opKey, value_json: {} });
  };

  const saveAllRulesToApi = async () => {
    const policyId = currentPolicyId || editingPolicy?.id;
    if (!policyId) {
      await showErrorAlert("Please save the policy title first before saving rules.");
      return;
    }

    setSavingRule(true);
    try {
      const newRules = [...builderRules];
      let currentGroupKey = genGroupKey();
      let currentPriority = 0;

      for (let i = 0; i < newRules.length; i++) {
        const isNewGroup = i === 0 || !newRules[i].rule_join_key;
        if (isNewGroup) {
          currentGroupKey = newRules[i].rule_group_key || genGroupKey();
          currentPriority++;
        }

        const payload = {
          tenant_id: selectedTenantId!,
          approval_policy_id: policyId,
          related_entity_name: "proposal",
          entity_field_name: newRules[i].entity_field_name,
          operator_key: newRules[i].operator_key,
          value_json: newRules[i].value_json,
          priority: currentPriority,
          created_by: auth?.user_id,
          updated_by: auth?.user_id,
          rule_group_key: currentGroupKey,
          rule_join_key: newRules[i].rule_join_key || null,
        };

        if (newRules[i].apiId) {
          await ApprovalPolicyService.updateRule(newRules[i].apiId!, payload);
          newRules[i] = { ...newRules[i], rule_group_key: currentGroupKey, priority: currentPriority };
        } else {
          const created = await ApprovalPolicyService.createRule(payload);
          newRules[i] = { ...newRules[i], apiId: created.approval_policy_rule_id, rule_group_key: currentGroupKey, priority: currentPriority };
        }
      }
      setBuilderRules(newRules);
      await showSuccessAlert("Rules saved successfully.");
      setExpandedRuleId(null);
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to save rules.");
    } finally {
      setSavingRule(false);
    }
  };

  const removeRule = async (id: string) => {
    const rule = builderRules.find((r) => r.id === id);
    if (!rule) return;

    const result = await Swal.fire({
      title: "Delete Rule?",
      text: "Are you sure you want to delete this rule?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      if (rule.apiId) {
        try {
          await ApprovalPolicyService.deleteRule(rule.apiId);
        } catch (err) {
          console.error(err);
          await showErrorAlert("Failed to delete rule from backend.");
          return;
        }
      }
      setBuilderRules((prev) => prev.filter((r) => r.id !== id));
      if (expandedRuleId === id) setExpandedRuleId(null);
      await showSuccessAlert("Rule deleted successfully.");
    }
  };

  const addStage = () => {
    const newStage: StageItem = {
      id: genId(),
      name: "",
      order: builderStages.length + 1,
      condition: conditionOptions.length > 0 ? conditionOptions[0].approval_stage_condition_key : "",
      approver: approverOptions.length > 0 ? approverOptions[0].approval_actor_type_key : "",
    };
    setBuilderStages((prev) => [...prev, newStage]);
    setEditingStageId(newStage.id);
    setStageName("");
    setStageOrder(builderStages.length + 1);
    setStageCondition(conditionOptions.length > 0 ? conditionOptions[0].approval_stage_condition_key : "");
    setStageApprover(approverOptions.length > 0 ? approverOptions[0].approval_actor_type_key : "");
    setExternalEmailInput("");
  };

  const handleRuleJoinSelect = (idx: number, joinKey: string) => {
    setBuilderRules((prev) => {
      const newRules = [...prev];
      if (joinKey) {
        for (let i = 0; i < newRules.length; i++) {
          if (newRules[i].rule_join_key) {
            newRules[i] = { ...newRules[i], rule_join_key: joinKey };
          }
        }
      }
      newRules[idx] = { ...newRules[idx], rule_join_key: joinKey };
      return newRules;
    });
  };

  const startEditStage = (stage: StageItem) => {
    setEditingStageId(stage.id);
    setStageName(stage.name);
    setStageOrder(stage.order);
    setStageCondition(stage.condition);
    setStageApprover(stage.approver);
    setStageApproverIdentities(stage.approverIdentities || []);
    setExternalEmailInput("");
  };

  const saveStageEdit = async () => {
    if (!editingStageId) return;

    const policyId = currentPolicyId || editingPolicy?.id;
    if (!policyId) {
      await showErrorAlert("Please save the Policy first before adding stages.");
      return;
    }

    if (!selectedTenantId) {
      await showErrorAlert("Tenant ID is required.");
      return;
    }

    const stage = builderStages.find(s => s.id === editingStageId);
    if (!stage) return;

    if (!stageName.trim()) {
      await showErrorAlert("Stage Name is required.");
      return;
    }

    setSavingStage(true);
    try {
      const conditionObj = conditionOptions.find(c => c.approval_stage_condition_key === stageCondition);
      const conditionId = conditionObj?.approval_stage_condition_id || 1;

      const stagePayload = {
        tenant_id: selectedTenantId,
        approval_policy_id: policyId,
        stage_order: stageOrder,
        stage_name: stageName,
        approval_stage_condition_id: conditionId,
      };

      let savedStageId = stage.apiId;

      if (savedStageId) {
        await ApprovalPolicyService.updateStage(savedStageId, stagePayload);
      } else {
        const createdStage = await ApprovalPolicyService.createStage(stagePayload);
        savedStageId = createdStage.approval_policy_stage_id;
      }

      const actorObj = approverOptions.find(a => a.approval_actor_type_key === stageApprover);
      const actorId = actorObj?.approval_actor_type_id || 1;

      const finalIdentities = [...stageApproverIdentities];

      const approverPayload = {
        tenant_id: selectedTenantId,
        approval_policy_stage_id: savedStageId,
        approval_actor_type_id: actorId,
        approver_identities: finalIdentities,
      };

      await ApprovalPolicyService.saveStageApprover(approverPayload);

      setBuilderStages((prev) =>
        prev.map((s) =>
          s.id === editingStageId
            ? {
              ...s,
              name: stageName,
              order: stageOrder,
              condition: stageCondition,
              approver: stageApprover,
              apiId: savedStageId,
              approverIdentities: finalIdentities,
            }
            : s
        )
      );
      setEditingStageId(null);
      await showSuccessAlert("Stage saved successfully.");
    } catch (err) {
      console.error("Failed to save stage", err);
      await showErrorAlert("Failed to save stage. Please try again.");
    } finally {
      setSavingStage(false);
    }
  };

  const cancelStageEdit = () => {
    const stage = builderStages.find((s) => s.id === editingStageId);
    if (stage && !stage.name) {
      setBuilderStages((prev) => prev.filter((s) => s.id !== editingStageId));
    }
    setEditingStageId(null);
  };

  const removeStage = async (id: string) => {
    const stage = builderStages.find((s) => s.id === id);
    if (!stage) return;

    if (stage.apiId) {
      const result = await Swal.fire({
        title: "Delete Stage",
        text: "Are you sure you want to delete this stage? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        try {
          await ApprovalPolicyService.deleteStage(stage.apiId);
          setBuilderStages((prev) => prev.filter((s) => s.id !== id));
          if (editingStageId === id) setEditingStageId(null);
          await showSuccessAlert("Stage deleted successfully.");
        } catch (err) {
          console.error("Failed to delete stage", err);
          await showErrorAlert("Failed to delete stage. Please try again.");
        }
      }
    } else {
      // Not saved to API yet
      setBuilderStages((prev) => prev.filter((s) => s.id !== id));
      if (editingStageId === id) setEditingStageId(null);
    }
  };

  const savePolicy = async () => {
    if (!builderTitle.trim()) {
      await showErrorAlert("Please enter a policy title.");
      return;
    }

    const parsedPriority = Number(builderPriorityOrder) || 1;
    if (policies.some(p => p.priorityOrder === parsedPriority && String(p.id) !== String(currentPolicyId || editingPolicy?.id))) {
      await showErrorAlert("A policy with this priority order already exists.");
      return;
    }

    setSavingPolicy(true);

    try {
      if (editingPolicy || currentPolicyId) {
        const id = currentPolicyId || editingPolicy!.id;
        const updated = await ApprovalPolicyService.updatePolicy(id, {
          approval_policy_name: builderTitle.trim(),
          is_active: builderActive,
          is_manager_approval_needed: builderRequireManagerApproval,
          priority_order: parsedPriority,
          related_entity_name: "proposal",
          tenant_id: selectedTenantId!,
          updated_by: auth?.user_id,
        });
        const policyData: Policy = {
          id: String(updated.approval_policy_id),
          title: updated.approval_policy_name,
          isActive: updated.is_active,
          requiresManagerApproval: updated.is_manager_approval_needed,
          priorityOrder: parsedPriority,
          rules: builderRules,
          stages: builderStages,
        };
        setPolicies((prev) =>
          prev.map((p) => (p.id === String(id) ? policyData : p))
        );
      } else {
        const created = await ApprovalPolicyService.createPolicy({
          approval_policy_name: builderTitle.trim(),
          is_active: builderActive,
          is_manager_approval_needed: builderRequireManagerApproval,
          priority_order: parsedPriority,
          related_entity_name: "proposal",
          tenant_id: selectedTenantId!,
          created_by: auth?.user_id,
          updated_by: auth?.user_id,
        });
        const policyData: Policy = {
          id: String(created.approval_policy_id),
          title: created.approval_policy_name,
          isActive: created.is_active,
          requiresManagerApproval: created.is_manager_approval_needed,
          priorityOrder: parsedPriority,
          rules: builderRules,
          stages: builderStages,
        };
        setPolicies((prev) => [...prev, policyData]);
      }

      await showSuccessAlert(
        editingPolicy ? "Policy updated successfully." : "Policy created successfully."
      );
      resetBuilder();
      setView("list");
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to save policy.");
    } finally {
      setSavingPolicy(false);
    }
  };

  const getDisplayValue = (rule: RuleItem) => {
    if (rule.operator_key.toUpperCase() === "BETWEEN") {
      return `${rule.value_json?.min ?? ""} - ${rule.value_json?.max ?? ""}`;
    }
    return String(rule.value_json?.value ?? "");
  };

  const getOperatorDisplay = (opKey: string) => {
    const op = operatorOptions.find(
      (o) => o.approval_rule_operator_key === opKey
    );
    if (op) return op.approval_rule_operator_display_name;
    return OPERATOR_DISPLAY[opKey.toUpperCase()] || opKey;
  };

  const getFieldDisplay = (fieldName: string) => {
    const field = entityFields.find((f) => f.field_name === fieldName);
    return field?.display_name || fieldName;
  };

  const renderRuleValueInput = (rule: RuleItem) => {
    const field = entityFields.find((f) => f.field_name === rule.entity_field_name);
    const dataType = field?.data_type;

    if (rule.entity_field_name.startsWith("is_")) {
      return (
        <select
          className="ap-field-select"
          value={rule.value_json?.value || ""}
          onChange={(e) => updateRule(rule.id, { value_json: { value: e.target.value } })}
        >
          <option value="">Select option</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (rule.entity_field_name === "currency_code") {
      return (
        <CurrencySelect
          value={rule.value_json?.value || ""}
          onChange={(v) => updateRule(rule.id, { value_json: { value: v } })}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
            menu: (base: any) => ({ ...base, zIndex: 99999 }),
            control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38 }),
          }}
        />
      );
    }

    if (isDateField(rule.entity_field_name)) {
      return (
        <input
          className="ap-field-input"
          type="datetime-local"
          placeholder="Enter value"
          value={rule.value_json?.value || ""}
          onChange={(e) => updateRule(rule.id, { value_json: { value: e.target.value } })}
        />
      );
    }

    if (dataType === "int") {
      return (
        <input
          className="ap-field-input"
          type="number"
          step="1"
          placeholder="Enter integer value"
          value={rule.value_json?.value ?? ""}
          onChange={(e) => updateRule(rule.id, { value_json: { value: e.target.value !== "" ? Number(e.target.value) : "" } })}
        />
      );
    }

    if (dataType === "float") {
      return (
        <input
          className="ap-field-input"
          type="number"
          step="any"
          placeholder="Enter number value"
          value={rule.value_json?.value ?? ""}
          onChange={(e) => updateRule(rule.id, { value_json: { value: e.target.value !== "" ? Number(e.target.value) : "" } })}
        />
      );
    }

    return (
      <input
        className="ap-field-input"
        type="text"
        placeholder="Enter value"
        value={rule.value_json?.value || ""}
        onChange={(e) => updateRule(rule.id, { value_json: { value: e.target.value } })}
      />
    );
  };

  const renderRuleBetweenInputs = (rule: RuleItem) => {
    const field = entityFields.find((f) => f.field_name === rule.entity_field_name);
    const dataType = field?.data_type;
    const isDate = isDateField(rule.entity_field_name);
    const isNumeric = dataType === "int" || dataType === "float";
    const inputType = isDate ? "datetime-local" : isNumeric ? "number" : "text";
    const step = dataType === "int" ? "1" : dataType === "float" ? "any" : undefined;
    const parseVal = (v: string) => (isNumeric && v !== "" ? Number(v) : v);

    return (
      <div className="ap-between-column">
        <div className="ap-field-group">
          <label className="ap-field-label">Min</label>
          <input
            className="ap-field-input"
            type={inputType}
            step={step}
            placeholder="Min value"
            value={rule.value_json?.min ?? ""}
            onChange={(e) => updateRule(rule.id, { value_json: { ...rule.value_json, min: parseVal(e.target.value) } })}
          />
        </div>
        <div className="ap-field-group">
          <label className="ap-field-label">Max</label>
          <input
            className="ap-field-input"
            type={inputType}
            step={step}
            placeholder="Max value"
            value={rule.value_json?.max ?? ""}
            onChange={(e) => updateRule(rule.id, { value_json: { ...rule.value_json, max: parseVal(e.target.value) } })}
          />
        </div>
      </div>
    );
  };

  const renderListView = () => (
    <>
      <div className="ap-list-header">
        <h4>Approval Policies</h4>
        {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
          <button className="ap-create-btn" onClick={openCreate}>
            <i className="fa fa-plus" />
            Create Policy
          </button>
        )}
      </div>

      {loadingPolicies ? (
        <div className="ap-empty-state">
          <div className="ap-empty-icon">
            <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} />
          </div>
          <div className="ap-empty-title">Loading...</div>
        </div>
      ) : policies.length === 0 ? (
        <div className="ap-empty-state">
          <div className="ap-empty-icon">
            <i className="ti ti-shield-check" />
          </div>
          <div className="ap-empty-title">No Approval Policies</div>
          <p className="ap-empty-desc">
            Create your first approval policy to automate decision workflows
            across your organization.
          </p>
          {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
            <button className="ap-create-btn" onClick={openCreate}>
              <i className="fa fa-plus" />
              Create Policy
            </button>
          )}
        </div>
      ) : (
        <div className="ap-policy-grid">
          {policies.map((policy) => (
            <div key={policy.id} className={`ap-policy-card ${policy.isActive ? "ap-card-active" : "ap-card-inactive"}`} style={{ zIndex: openMenuId === policy.id ? 100 : 1 }}>
              <div className="ap-card-header">
                <div className="ap-card-icon">
                  <i className="ti ti-shield-check" />
                </div>
                <div style={{ position: "relative" }}>
                  <button
                    className="ap-card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === policy.id ? null : policy.id);
                    }}
                  >
                    <i className="ti ti-dots-vertical" />
                  </button>
                  {openMenuId === policy.id && (
                    <div className="ap-card-dropdown">
                      {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
                        <button
                          className="ap-dropdown-item"
                          onClick={() => openEdit(policy)}
                        >
                          <i className="fa fa-edit" />
                          Edit
                        </button>
                      )}
                      <button
                        className="ap-dropdown-item"
                        onClick={() => handleViewDetails(policy)}
                      >
                        <i className="ti ti-eye" />
                        View Details
                      </button>
                      {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
                        <button
                          className="ap-dropdown-item ap-dropdown-danger"
                          onClick={() => handleDelete(policy.id)}
                        >
                          <i className="ti ti-trash" />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div
                className="ap-card-title"
                onClick={() => handleViewDetails(policy)}
                style={{ cursor: "pointer" }}
                title="View Details"
              >
                {policy.title}
              </div>
              <div className="ap-card-badges-bottom">
                <span className={`ap-badge ${policy.isActive ? "ap-badge-active" : "ap-badge-inactive"}`}>
                  {policy.isActive ? "Active" : "Inactive"}
                </span>
                {policy.requiresManagerApproval && (
                  <span className="ap-badge ap-badge-approval">Manager Approval</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderFlowCanvas = () => (
    <div className="ap-builder-canvas">
      <div className="ap-flow-container">
        <div
          className={`ap-flow-node ap-flow-node-title ${selectedNode === "title" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeSwitch("title")}
        >
          <div className="ap-flow-node-label">
            <i className="ti ti-file-text" />
            {builderTitle || "Title"}
          </div>
        </div>

        <div className="ap-connector">
          <div className="ap-connector-line" />
        </div>

        <div
          className={`ap-flow-node ap-flow-node-rule ${selectedNode === "rules" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeSwitch("rules")}
        >
          <div className="ap-flow-node-label">
            <i className="fa fa-filter" />
            Rules
          </div>
          {builderRules.length > 0 && (
            <div className="ap-flow-node-sublabel">{builderRules.length} rule{builderRules.length !== 1 ? "s" : ""}</div>
          )}
        </div>

        <div className="ap-connector">
          <div className="ap-connector-line" />
        </div>

        <div
          className={`ap-flow-node ap-flow-node-stage ${selectedNode === "stages" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeSwitch("stages")}
        >
          <div className="ap-flow-node-label">
            <i className="ti ti-git-merge" />
            Stages
          </div>
          {builderStages.length > 0 && (
            <div className="ap-flow-node-sublabel">{builderStages.length} stage{builderStages.length !== 1 ? "s" : ""}</div>
          )}
        </div>
      </div>
    </div>
  );

  const renderRuleConnector = (index: number) => {
    if (index === 0) return null;
    const rule = builderRules[index];
    const joinKey = rule.rule_join_key;

    return (
      <div className="ap-rule-connector">
        <div className="ap-join-picker">
          <button
            className={`ap-join-picker-btn ${joinKey === "AND" ? "ap-join-picker-btn-active-and" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleRuleJoinSelect(index, joinKey === "AND" ? "" : "AND");
            }}
          >
            AND
          </button>
          <button
            className={`ap-join-picker-btn ${joinKey === "OR" ? "ap-join-picker-btn-active-or" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleRuleJoinSelect(index, joinKey === "OR" ? "" : "OR");
            }}
          >
            OR
          </button>
        </div>
      </div>
    );
  };

  const renderPropsPanel = () => (
    <div className="ap-props-panel">
      {!selectedNode ? (
        <div className="ap-props-empty">
          <div className="ap-props-empty-icon">
            <i className="ti ti-click" />
          </div>
          <p>Select a node on the canvas to view and edit its properties</p>
        </div>
      ) : selectedNode === "title" ? (
        <>
          <div className="ap-props-header">
            <h5>
              <span className="ap-props-header-icon ap-props-icon-title">
                <i className="ti ti-file-text" />
              </span>
              Policy
            </h5>
          </div>
          <div className="ap-props-body">
            <div className="ap-field-group">
              <label className="ap-field-label">Title</label>
              <input
                className="ap-field-input"
                type="text"
                placeholder="Enter policy name"
                value={builderTitle}
                onChange={(e) => setBuilderTitle(e.target.value)}
              />
            </div>
            <div className="ap-field-group">
              <label className="ap-field-label">Priority Order</label>
              <input
                className="ap-field-input"
                type="number"
                min={1}
                step="1"
                placeholder="Enter priority order"
                value={builderPriorityOrder}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setBuilderPriorityOrder("");
                  } else {
                    const num = Number(val);
                    if (!isNaN(num)) {
                      setBuilderPriorityOrder(Math.max(1, num));
                    }
                  }
                }}
              />
              {builderPriorityOrder !== "" && policies.some(p => p.priorityOrder === builderPriorityOrder && String(p.id) !== String(currentPolicyId || editingPolicy?.id)) && (
                <span className="ap-field-hint" style={{ color: "#f59e0b", marginTop: 4, display: "block" }}>
                  A policy with this priority order already exists.
                </span>
              )}
            </div>

            <div className="ap-toggle-row">
              <div className="ap-toggle-info">
                <span className="ap-toggle-title">Active</span>
                <span className="ap-toggle-desc">
                  Enable or disable this policy
                </span>
              </div>
              <label className="ap-switch">
                <input
                  type="checkbox"
                  checked={builderActive}
                  onChange={(e) => setBuilderActive(e.target.checked)}
                />
                <span className="ap-switch-slider" />
              </label>
            </div>
            <div className="ap-toggle-row">
              <div className="ap-toggle-info">
                <span className="ap-toggle-title">Manager Approval Required</span>
                <span className="ap-toggle-desc">
                  Team manager will approve the request
                </span>
              </div>
              <label className="ap-switch">
                <input
                  type="checkbox"
                  checked={builderRequireManagerApproval}
                  onChange={(e) => setBuilderRequireManagerApproval(e.target.checked)}
                />
                <span className="ap-switch-slider" />
              </label>
            </div>
          </div>
          <div className="ap-props-footer">
            <button
              className="ap-btn-cancel"
              onClick={() => {
                const snap = titleSnapshotRef.current;
                setBuilderTitle(snap.title);
                setBuilderActive(snap.active);
                setBuilderRequireManagerApproval(snap.managerApproval);
                setBuilderPriorityOrder(snap.priorityOrder);
                setTitleDirty(false);
              }}
            >
              Cancel
            </button>
            <button className="ap-btn-save" onClick={saveTitleSection} disabled={savingPolicy || builderPriorityOrder === "" || policies.some(p => p.priorityOrder === builderPriorityOrder && String(p.id) !== String(currentPolicyId || editingPolicy?.id))}>
              {savingPolicy ? "Saving..." : editingPolicy || currentPolicyId ? "Update" : "Save"}
            </button>
          </div>
        </>
      ) : selectedNode === "rules" ? (
        <>
          <div className="ap-props-header">
            <h5>
              <span className="ap-props-header-icon ap-props-icon-rule">
                <i className="fa fa-filter" />
              </span>
              Rules
            </h5>
          </div>
          <div className="ap-props-body">
            {loadingRules ? (
              <div className="ap-panel-empty-hint">
                <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} />
                <p>Loading rules...</p>
              </div>
            ) : (
              <>
                {builderRules.length > 0 && (
                  <div className="ap-panel-list">
                    {builderRules.map((rule, idx) => (
                      <React.Fragment key={rule.id}>
                        {renderRuleConnector(idx)}
                        <div className={`ap-panel-list-item ${expandedRuleId === rule.id ? "ap-panel-item-editing" : ""}`}>
                          {expandedRuleId === rule.id ? (
                            <div className="ap-panel-edit-form">
                              <div className="ap-field-group">
                                <label className="ap-field-label">Field</label>
                                <select
                                  className="ap-field-select"
                                  value={rule.entity_field_name}
                                  onChange={(e) => handleFieldChange(rule.id, e.target.value)}
                                >
                                  {entityFields.map((f) => (
                                    <option key={f.field_name} value={f.field_name}>
                                      {f.display_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="ap-field-group">
                                <label className="ap-field-label">Operator</label>
                                <select
                                  className="ap-field-select"
                                  value={rule.operator_key}
                                  onChange={(e) => handleOperatorChange(rule.id, e.target.value)}
                                >
                                  {getFilteredOperators(rule.allowedOperators).map((op) => (
                                    <option key={op.approval_rule_operator_key} value={op.approval_rule_operator_key}>
                                      {op.approval_rule_operator_display_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {rule.operator_key.toUpperCase() === "BETWEEN" ? (
                                renderRuleBetweenInputs(rule)
                              ) : (
                                <div className="ap-field-group">
                                  <label className="ap-field-label">Value</label>
                                  {renderRuleValueInput(rule)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="ap-panel-list-row">
                              <div className="ap-panel-list-info">
                                <span className="ap-panel-list-num">#{idx + 1}</span>
                                <div>
                                  <span className="ap-panel-list-text">
                                    {getFieldDisplay(rule.entity_field_name) || "Unconfigured rule"}
                                  </span>
                                </div>
                              </div>
                              <div className="ap-panel-list-actions">
                                <button className="ap-panel-icon-btn" onClick={() => setExpandedRuleId(rule.id)} title="Edit">
                                  <i className="fa fa-edit" />
                                </button>
                                <button className="ap-panel-icon-btn ap-panel-icon-danger" onClick={() => removeRule(rule.id)} title="Remove">
                                  <i className="ti ti-trash" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {builderRules.length === 0 && (
                  <div className="ap-panel-empty-hint">
                    <i className="ti ti-filter-off" />
                    <p>No rules added yet</p>
                  </div>
                )}

                <button className="ap-panel-add-btn" onClick={addRule}>
                  <i className="fa fa-plus" />
                  Add Rule
                </button>
              </>
            )}
          </div>
          <div className="ap-props-footer">
            <button
              className="ap-btn-cancel"
              onClick={() => handleNodeSwitch("title")}
            >
              Back
            </button>
            <button className="ap-btn-save" onClick={saveAllRulesToApi} disabled={savingRule}>
              {savingRule ? "Saving..." : "Save Rules"}
            </button>
          </div>
        </>
      ) : selectedNode === "stages" ? (
        <>
          <div className="ap-props-header">
            <h5>
              <span className="ap-props-header-icon ap-props-icon-stage">
                <i className="ti ti-git-merge" />
              </span>
              Stages
            </h5>
          </div>
          <div className="ap-props-body">
            {builderStages.length > 0 && (
              <div className="ap-panel-list">
                {builderStages.map((stage, idx) => (
                  <div key={stage.id} className={`ap-panel-list-item ${editingStageId === stage.id ? "ap-panel-item-editing" : ""}`}>
                    {editingStageId === stage.id ? (
                      <div className="ap-panel-edit-form">
                        <div className="ap-field-group">
                          <label className="ap-field-label">Stage Name</label>
                          <input
                            className="ap-field-input"
                            type="text"
                            placeholder="e.g. Manager Review"
                            value={stageName}
                            onChange={(e) => setStageName(e.target.value)}
                          />
                        </div>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Order</label>
                          <input
                            className="ap-field-input"
                            type="number"
                            min={1}
                            value={stageOrder}
                            onChange={(e) => setStageOrder(Number(e.target.value))}
                          />
                        </div>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Condition</label>
                          <select
                            className="ap-field-select"
                            value={stageCondition}
                            onChange={(e) => setStageCondition(e.target.value)}
                          >
                            {conditionOptions.map((opt) => (
                              <option key={opt.approval_stage_condition_key} value={opt.approval_stage_condition_key}>
                                {opt.approval_stage_condition_display_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Approver</label>
                          <select
                            className="ap-field-select"
                            value={stageApprover}
                            onChange={(e) => {
                              setStageApprover(e.target.value);
                              setStageApproverIdentities([]);
                            }}
                          >
                            {approverOptions.map((opt) => (
                              <option key={opt.approval_actor_type_key} value={opt.approval_actor_type_key}>
                                {opt.approval_actor_type_display_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {stageApprover === "TEAM_MANAGER" && (
                          <div className="ap-field-group">
                            <span style={{ fontSize: 13, color: "#64748b" }}>The Team manager of assigned team will approve</span>
                          </div>
                        )}
                        {stageApprover === "ASSIGNED_USER" && (
                          <div className="ap-field-group">
                            <span style={{ fontSize: 13, color: "#64748b" }}>Assigned user will approve</span>
                          </div>
                        )}
                        {stageApprover === "SALESUSER" && (
                          <div className="ap-field-group">
                            <label className="ap-field-label">Select Sales Users</label>
                            <Select
                              isMulti
                              hideSelectedOptions={false}
                              closeMenuOnSelect={false}
                              components={{ Option: CustomOption }}
                              options={tenantUsers.filter(u => u.role_display_name === "Sales User").map(user => ({
                                value: user.user_id,
                                label: `${user.user_first_name} ${user.user_last_name} (${user.email})`
                              }))}
                              value={tenantUsers
                                .filter(u => u.role_display_name === "Sales User" && stageApproverIdentities.includes(u.user_id))
                                .map(user => ({
                                  value: user.user_id,
                                  label: `${user.user_first_name} ${user.user_last_name} (${user.email})`
                                }))}
                              onChange={(selectedOptions) => {
                                setStageApproverIdentities(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
                              }}
                              placeholder="Select sales users..."
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  fontSize: 13,
                                  minHeight: 38,
                                }),
                                menu: (base) => ({
                                  ...base,
                                  fontSize: 13,
                                  zIndex: 9999
                                })
                              }}
                            />
                            {tenantUsers.filter(u => u.role_display_name === "Sales User").length === 0 && (
                              <span style={{ fontSize: 13, color: "#64748b", marginTop: 4, display: "block" }}>No sales users found.</span>
                            )}
                          </div>
                        )}
                        {stageApprover === "CLIENT_ADMIN" && (
                          <div className="ap-field-group">
                            <label className="ap-field-label">Select Client Admins</label>
                            <Select
                              isMulti
                              hideSelectedOptions={false}
                              closeMenuOnSelect={false}
                              components={{ Option: CustomOption }}
                              options={tenantUsers.filter(u => u.role_display_name === "Client Admin").map(user => ({
                                value: user.user_id,
                                label: `${user.user_first_name} ${user.user_last_name} (${user.email})`
                              }))}
                              value={tenantUsers
                                .filter(u => u.role_display_name === "Client Admin" && stageApproverIdentities.includes(u.user_id))
                                .map(user => ({
                                  value: user.user_id,
                                  label: `${user.user_first_name} ${user.user_last_name} (${user.email})`
                                }))}
                              onChange={(selectedOptions) => {
                                setStageApproverIdentities(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
                              }}
                              placeholder="Select client admins..."
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  fontSize: 13,
                                  minHeight: 38,
                                }),
                                menu: (base) => ({
                                  ...base,
                                  fontSize: 13,
                                  zIndex: 9999
                                })
                              }}
                            />
                            {tenantUsers.filter(u => u.role_display_name === "Client Admin").length === 0 && (
                              <span style={{ fontSize: 13, color: "#64748b", marginTop: 4, display: "block" }}>No client admins found.</span>
                            )}
                          </div>
                        )}
                        {stageApprover === "EXTERNAL_APPROVER" && (
                          <div className="ap-field-group">
                            <label className="ap-field-label">External Emails</label>
                            <div className="ap-chip-input-container" style={{ display: "flex", flexWrap: "wrap", gap: 8, border: "1px solid #e2e8f0", borderRadius: 4, padding: "4px 8px", minHeight: 38 }}>
                              {stageApproverIdentities.map((email, idx) => (
                                <span key={idx} style={{ background: "#e2e8f0", padding: "2px 8px", borderRadius: 12, fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }} onClick={() => {
                                  setExternalEmailInput(String(email));
                                  setStageApproverIdentities(prev => prev.filter((_, i) => i !== idx));
                                }}>
                                  {email}
                                  <i className="ti ti-x" style={{ cursor: "pointer" }} onClick={(e) => {
                                    e.stopPropagation();
                                    setStageApproverIdentities(prev => prev.filter((_, i) => i !== idx));
                                  }} />
                                </span>
                              ))}
                              <input
                                type="text"
                                value={externalEmailInput}
                                onChange={(e) => setExternalEmailInput(e.target.value)}
                                style={{ border: "none", outline: "none", flex: 1, minWidth: 100, fontSize: 13 }}
                                placeholder="Add email..."
                                onBlur={(e) => {
                                  const val = e.currentTarget.value.trim();
                                  if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                                    if (!stageApproverIdentities.includes(val)) {
                                      setStageApproverIdentities(prev => [...prev, val]);
                                    }
                                    setExternalEmailInput("");
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === ",") {
                                    e.preventDefault();
                                    const val = e.currentTarget.value.trim();
                                    if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                                      if (!stageApproverIdentities.includes(val)) {
                                        setStageApproverIdentities(prev => [...prev, val]);
                                      }
                                      setExternalEmailInput("");
                                    }
                                  }
                                }}
                              />
                            </div>
                            <span className="ap-field-hint">Press Enter, comma, or click outside to add an email. Click an email chip to edit.</span>
                          </div>
                        )}
                        <div className="ap-panel-edit-actions">
                          <button className="ap-btn-cancel ap-btn-sm" onClick={cancelStageEdit} disabled={savingStage}>Cancel</button>
                          <button className="ap-btn-save ap-btn-sm" onClick={saveStageEdit} disabled={savingStage}>
                            {savingStage ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ap-panel-list-row">
                        <div className="ap-panel-list-info">
                          <span className="ap-panel-list-num">#{idx + 1}</span>
                          <div>
                            <span className="ap-panel-list-text">
                              {stage.name || "Unnamed Stage"}
                            </span>
                            <span className="ap-panel-list-sub">
                              {approverOptions.find(a => a.approval_actor_type_key === stage.approver)?.approval_actor_type_display_name || stage.approver}
                            </span>
                          </div>
                        </div>
                        <div className="ap-panel-list-actions">
                          <button className="ap-panel-icon-btn" onClick={() => startEditStage(stage)} title="Edit">
                            <i className="fa fa-edit" />
                          </button>
                          <button className="ap-panel-icon-btn ap-panel-icon-danger" onClick={() => removeStage(stage.id)} title="Remove">
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {builderStages.length === 0 && (
              <div className="ap-panel-empty-hint">
                <i className="ti ti-git-merge" />
                <p>No stages added yet</p>
              </div>
            )}

            <button className="ap-panel-add-btn" onClick={addStage}>
              <i className="fa fa-plus" />
              Add Stage
            </button>
          </div>
          <div className="ap-props-footer">
            <button
              className="ap-btn-cancel"
              onClick={() => handleNodeSwitch("title")}
            >
              Back
            </button>
          </div>
        </>
      ) : null}
    </div>
  );

  const renderBuilderView = () => (
    <>
      <div className="ap-builder-header">
        <h4>
          <i
            className="ti ti-arrow-narrow-left"
            style={{ cursor: "pointer", opacity: 0.6 }}
            onClick={() => {
              resetBuilder();
              setView("list");
            }}
          />
          {editingPolicy ? "Edit Policy" : "Create Policy"}
        </h4>
        <button
          title="Quick Guide"
          onClick={() => setShowGuide(true)}
          style={{
            border: "none",
            background: "#f1f5f9",
            borderRadius: 8,
            width: 34,
            height: 34,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6366f1",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          <i className="ti ti-info-circle" />
        </button>
      </div>

      <div className="ap-builder-layout">
        {renderFlowCanvas()}
        {renderPropsPanel()}
      </div>
    </>
  );

  const renderDetailModal = () => {
    if (loadingDetail) {
      return (
        <div className="ap-detail-overlay">
          <div className="ap-detail-modal" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 50, maxWidth: 400 }}>
            <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite", fontSize: 24, color: "#6366f1" }} />
            <span style={{ marginLeft: 10 }}>Loading details...</span>
          </div>
        </div>
      );
    }
    if (!viewDetailPolicy) return null;
    const p = viewDetailPolicy;

    const groupedRules = p.rules.reduce((acc, rule) => {
      const key = rule.rule_group_key || 'ungrouped';
      if (!acc[key]) acc[key] = [];
      acc[key].push(rule);
      return acc;
    }, {} as Record<string, ApprovalRuleAPI[]>);
    const sortedStages = [...p.stages].sort((a, b) =>
      a.stage_order - b.stage_order ||
      a.approval_policy_stage_id - b.approval_policy_stage_id
    );
    const stageTimelineItems = sortedStages.reduce<{
      stage: ApprovalPolicyDetailAPI["stages"][number];
      approver: ApprovalPolicyDetailAPI["stage_approvers"][number] | null;
    }[]>((items, stage) => {
      const stageApprovers = p.stage_approvers.filter(a => a.approval_policy_stage_id === stage.approval_policy_stage_id);
      if (stageApprovers.length === 0) {
        items.push({ stage, approver: null });
      } else {
        stageApprovers.forEach((approver) => items.push({ stage, approver }));
      }
      return items;
    }, []);

    return (
      <div className="ap-detail-overlay" onClick={() => setViewDetailPolicy(null)}>
        <div className="ap-detail-modal" style={{ maxWidth: 1100, width: "100%", position: "relative" }} onClick={(e) => e.stopPropagation()}>
          <button
            className="ap-detail-close"
            onClick={() => setViewDetailPolicy(null)}
            style={{ position: "absolute", top: 16, right: 16, border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#64748b" }}
          >
            <i className="ti ti-x" />
          </button>
          <div className="ap-detail-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20, paddingRight: 32 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 22, color: "#1e293b", display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="ti ti-shield-check" style={{ color: "#6366f1" }} />
                  {p.approval_policy_name}
                </h3>
                <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                  <span className={`ap-badge ${p.is_active ? "ap-badge-active" : "ap-badge-inactive"}`}>
                    {p.is_active ? "Active" : "Inactive"}
                  </span>
                  {p.is_manager_approval_needed && (
                    <span className="ap-badge ap-badge-approval">Manager Approval Required</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="ap-detail-content ap-wrapper" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxHeight: "60vh", overflowY: "auto", paddingRight: 10 }}>

            <div className="ap-detail-card mb-4 mx-3" style={{ background: "#f8fafc", borderRadius: 12, padding: 20, border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: 16, color: "#334155", display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fa fa-filter" style={{ color: "#8b5cf6" }} />
                Policy Rules
              </h4>
              {p.rules.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: 14 }}>No rules defined for this policy.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Object.entries(groupedRules).map(([groupKey, rules], idx) => (
                    <div key={groupKey} style={{ background: "#ffffff", borderRadius: 8, padding: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Rule Group {idx + 1} {rules[0].priority ? `(Priority: ${rules[0].priority})` : ''}
                      </div>
                      {rules.map((rule, rIdx) => (
                        <React.Fragment key={rule.approval_policy_rule_id}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: "#334155", flexWrap: "wrap" }}>
                            <span style={{ background: "#f1f5f9", padding: "4px 8px", borderRadius: 4, fontWeight: 500, fontFamily: "monospace" }}>{rule.entity_field_name}</span>
                            <span style={{ color: "#8b5cf6", fontWeight: 600 }}>{rule.operator_key}</span>
                            <span style={{ background: "#e0e7ff", color: "#4338ca", padding: "4px 8px", borderRadius: 4, fontWeight: 500 }}>
                              {rule.value_json?.value || JSON.stringify(rule.value_json)}
                            </span>
                          </div>
                          {rIdx < rules.length - 1 && rule.rule_join_key && (
                            <div style={{ padding: "8px 0 8px 12px", color: "#94a3b8", fontSize: 12, fontWeight: 600 }}>
                              {rule.rule_join_key}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ap-detail-card mb-4" style={{ background: "#f8fafc", borderRadius: 12, padding: 20, border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: 16, color: "#334155", display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="ti ti-git-merge" style={{ color: "#10b981" }} />
                Approval Stages
              </h4>
              {p.stages.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: 14 }}>No stages defined for this policy.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {stageTimelineItems.map(({ stage, approver }, sIdx) => {
                    return (
                      <div key={`${stage.approval_policy_stage_id}-${approver?.approval_policy_stage_approver_id || "empty"}`} style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#10b981", color: "#fff", display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, zIndex: 1, flexShrink: 0 }}>
                            {sIdx + 1}
                          </div>
                          {sIdx < stageTimelineItems.length - 1 && <div style={{ flex: 1, width: 2, background: "#d1d5db", margin: "4px 0" }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: sIdx < stageTimelineItems.length - 1 ? 24 : 0 }}>
                          <div style={{ background: "#ffffff", borderRadius: 8, padding: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
                            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 15, marginBottom: 4 }}>{stage.stage_name}</div>
                            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
                              Condition: <span style={{ fontWeight: 500, color: "#334155" }}>{stage.approval_stage_condition?.approval_stage_condition_display_name || "N/A"}</span>
                            </div>

                            {approver && (
                              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Approver</div>
                                <div className="ap-detail-approver-groups">
                                  <div className="ap-detail-approver-group">
                                    <span className="ap-detail-actor-badge">
                                      {approver.approval_actor_type?.approval_actor_type_display_name}
                                    </span>
                                    {approver.approvers && approver.approvers.length > 0 ? (
                                      <div className="ap-detail-approver-list">
                                        {approver.approvers.map((person) => (
                                          <div key={person.user_id} className="ap-detail-approver-person">
                                            {person.avatar_url ? (
                                              <img
                                                className="ap-detail-approver-avatar"
                                                src={person.avatar_url}
                                                alt={person.full_name || person.email || "Approver"}
                                              />
                                            ) : (
                                              <span className="ap-detail-approver-avatar ap-detail-approver-avatar-fallback">
                                                {getApproverInitials(person.full_name, person.email)}
                                              </span>
                                            )}
                                            <span className="ap-detail-approver-copy">
                                              <span className="ap-detail-approver-name">
                                                {person.full_name || person.user_id
                                                  ? person.full_name || `User ${person.user_id}`
                                                  : ""
                                                }
                                              </span>
                                              {person.email && (
                                                <span className="ap-detail-approver-email">
                                                  {person.email}
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : approver.approver_identities && approver.approver_identities.length > 0 ? (
                                      <div className="ap-detail-identity-list">
                                        {approver.approver_identities.map((identity, i) => (
                                          <span key={`${identity}-${i}`} className="ap-detail-identity-chip">
                                            {identity}
                                          </span>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body pb-0 pt-2">
            <SettingsTabsNavigation
              activeTab="organization"
              variant="bottom"
              includeIcons={true}
              context="organization"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            <div className="card">
              <div className="card-body">
                <h4 className="fw-semibold mb-3">Organization Settings</h4>
                <div className="list-group list-group-flush settings-sidebar">
                  <Link to={route.companySettings} className="fw-medium">
                    General Settings
                  </Link>
                  <Link
                    to={route.approvalPolicy}
                    className="fw-medium active"
                  >
                    Approval Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body ap-wrapper">
                {view === "list" ? renderListView() : renderBuilderView()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderDetailModal()}
      <ApprovalPolicyGuide isOpen={showGuide} onClose={() => setShowGuide(false)} />
    </div>
  );
};

export default ApprovalPolicy;
